import React from 'react';
import { Card, CardBody, Spinner } from 'reactstrap';
import { RetentionCohortDto } from '@/services/analytics/analytics-types';

interface RetentionCohortsSectionProps {
  data?: RetentionCohortDto[];
  isLoading: boolean;
}

const RetentionCohortsSection: React.FC<RetentionCohortsSectionProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="p-5 text-center">
        <Spinner color="primary" />
        <p className="mt-2">Loading retention cohorts...</p>
      </div>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div className="p-5 text-center">No cohort data available</div>;
  }

  // Function to get color based on retention percentage
  const getRetentionColor = (retention: number): string => {
    if (retention >= 40) return 'bg-success';
    if (retention >= 20) return 'bg-warning';
    if (retention >= 10) return 'bg-info';
    return 'bg-danger';
  };

  // Function to get text color for better contrast
  const getTextColor = (retention: number): string => {
    if (retention >= 40) return 'text-white';
    if (retention >= 20) return 'text-dark';
    return 'text-white';
  };

  // Function to check if enough days have passed to show retention
  const canShowRetention = (cohortDate: string, daysAfter: number): boolean => {
    const cohortStart = new Date(cohortDate);
    const today = new Date();
    const daysDiff = Math.floor((today.getTime() - cohortStart.getTime()) / (1000 * 60 * 60 * 24));
    return daysDiff >= daysAfter;
  };

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h5 className="card-title mb-1">Weekly Retention Cohorts</h5>
            <p className="text-muted mb-0">
              Shows users grouped by their signup week and how many returned on specific days after signup
            </p>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table-hover mb-0 table">
            <thead className="table-light">
              <tr>
                <th>Signup Week</th>
                <th className="text-center">New Users</th>
                <th className="text-center" title="% of users who returned 1 day after signup">
                  Day 1
                </th>
                <th className="text-center" title="% of users who returned 7 days after signup">
                  Day 7
                </th>
                <th className="text-center" title="% of users who returned 30 days after signup">
                  Day 30
                </th>
                {Array.isArray(data) && data.some((cohort) => cohort.d60Retention !== undefined) && (
                  <th className="text-center" title="% of users who returned 60 days after signup">
                    Day 60
                  </th>
                )}
                {Array.isArray(data) && data.some((cohort) => cohort.d90Retention !== undefined) && (
                  <th className="text-center" title="% of users who returned 90 days after signup">
                    Day 90
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.map((cohort, index) => (
                <tr key={index}>
                  <td className="fw-medium">{cohort.cohort}</td>
                  <td className="text-center">
                    <span className="badge bg-primary">{cohort.users.toLocaleString()}</span>
                  </td>
                  <td className="text-center">
                    {canShowRetention(cohort.cohort, 1) ? (
                      <span
                        className={`badge ${getRetentionColor(cohort?.d1Retention)} ${getTextColor(cohort?.d1Retention)}`}
                        style={{ minWidth: '60px', display: 'inline-block' }}
                      >
                        {cohort?.d1Retention}%
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    {canShowRetention(cohort.cohort, 7) ? (
                      <span
                        className={`badge ${getRetentionColor(cohort?.d7Retention)} ${getTextColor(cohort?.d7Retention)}`}
                        style={{ minWidth: '60px', display: 'inline-block' }}
                      >
                        {cohort?.d7Retention}%
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    {canShowRetention(cohort.cohort, 30) ? (
                      <span
                        className={`badge ${getRetentionColor(cohort?.d30Retention)} ${getTextColor(cohort?.d30Retention)}`}
                        style={{ minWidth: '60px', display: 'inline-block' }}
                      >
                        {cohort?.d30Retention}%
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  {Array.isArray(data) && data.some((c) => c.d60Retention !== undefined) && (
                    <td className="text-center">
                      {canShowRetention(cohort.cohort, 60) && cohort?.d60Retention !== undefined ? (
                        <span
                          className={`badge ${getRetentionColor(cohort?.d60Retention)} ${getTextColor(cohort?.d60Retention)}`}
                          style={{ minWidth: '60px', display: 'inline-block' }}
                        >
                          {cohort?.d60Retention}%
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  )}
                  {Array.isArray(data) && data.some((c) => c.d90Retention !== undefined) && (
                    <td className="text-center">
                      {canShowRetention(cohort.cohort, 90) && cohort?.d90Retention !== undefined ? (
                        <span
                          className={`badge ${getRetentionColor(cohort.d90Retention)} ${getTextColor(cohort.d90Retention)}`}
                          style={{ minWidth: '60px', display: 'inline-block' }}
                        >
                          {cohort?.d90Retention}%
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Info Box */}
        <div className="alert alert-info mt-4" role="alert">
          <h6 className="alert-heading">How to read this data:</h6>
          <ul className="mb-0">
            <li>Each row represents users who signed up in a specific week</li>
            <li>The percentages show how many of those users came back after X days</li>
            <li>
              For example: If 100 users signed up in week "2025-06-30" and Day 7 shows 10%, it means 10 users returned 7
              days after their signup
            </li>
            <li>Higher retention percentages indicate better user engagement</li>
            <li className="mt-2">
              <strong>Note:</strong> Only weeks with actual user signups are shown. If you see fewer weeks than
              expected, it means there were no new user registrations during the other weeks in your selected date
              range.
            </li>
            <li>
              <strong>Future data:</strong> Retention percentages are only shown for periods that have already passed.
              For recent cohorts, some retention data may show "-" because not enough time has elapsed.
            </li>
          </ul>
        </div>

        <div className="mt-4">
          <h6 className="mb-3">Retention Benchmarks</h6>
          <div className="d-flex flex-wrap gap-3">
            <div className="d-flex align-items-center">
              <span className="badge bg-success me-2">●</span>
              <span className="text-muted">Excellent (≥40%)</span>
            </div>
            <div className="d-flex align-items-center">
              <span className="badge bg-warning me-2">●</span>
              <span className="text-muted">Good (20-39%)</span>
            </div>
            <div className="d-flex align-items-center">
              <span className="badge bg-info me-2">●</span>
              <span className="text-muted">Average (10-19%)</span>
            </div>
            <div className="d-flex align-items-center">
              <span className="badge bg-danger me-2">●</span>
              <span className="text-muted">Needs Improvement (≤10%)</span>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default RetentionCohortsSection;
